import { useCallback, useEffect, useMemo, useState } from "react";
import { checkoutCopy, checkoutLocale } from "../../../i18n/checkout/es";
import styles from "../../../pages/checkout/CheckoutPage.module.css";
import type { PaymentMethod, PaymentMethodField } from "../../../../domain/entities/PaymentMethod";
import type { Order } from "../../../../domain/entities/Order";
import { isPaymentMobileCode } from "../../../../domain/services/paymentMethodService";
import { PaymentMethodForm } from "./PaymentMethodForm";
import { PaymentMethodOption } from "./PaymentMethodOption";
import { PaymentMethodTransferInfo } from "./PaymentMethodTransferInfo";
import { PaymentVoucherUpload } from "./PaymentVoucherUpload";
import { StripePaymentFields, type StripePaymentController, type StripePaymentStatus } from "./StripePaymentFields";

interface PaymentMethodsSectionProps {
  paymentMethods: PaymentMethod[];
  selectedPaymentMethodCode: string | null;
  selectedPaymentMethod: PaymentMethod | null;
  order: Order;
  paymentFormFields: PaymentMethodField[];
  paymentFormValues: Record<string, string>;
  voucherFile: File | null;
  exchangeRate: number | null;
  isRateLoading: boolean;
  rateError: string | null;
  onSelect: (paymentMethodId: string) => void;
  onFieldChange: (fieldId: string, value: string) => void;
  onVoucherChange: (file: File | null) => void;
  onSubmit: (paymentTokenOverride?: string) => Promise<void>;
  isProcessing: boolean;
    actionError: string | null;
}

const STRIPE_METHOD_CODE = "PRW-STRIPE";
const DEFAULT_STRIPE_STATUS: StripePaymentStatus = { isComplete: false, hasError: false };

export const PaymentMethodsSection = ({
    paymentMethods,
  selectedPaymentMethodCode,
  selectedPaymentMethod,
  order,
  paymentFormFields,
  paymentFormValues,
  voucherFile,
  exchangeRate,
  isRateLoading,
  rateError,
  onSelect,
  onFieldChange,
  onVoucherChange,
  onSubmit,
  isProcessing,
    actionError,
}: PaymentMethodsSectionProps) => {
  const [stripeController, setStripeController] = useState<StripePaymentController | null>(null);
  const [stripeStatus, setStripeStatus] = useState<StripePaymentStatus>(DEFAULT_STRIPE_STATUS);
  const [stripeExternalError, setStripeExternalError] = useState<string | null>(null);

  const isStripeSelected = selectedPaymentMethod?.code === STRIPE_METHOD_CODE;
  const isVoucherRequired = Boolean(selectedPaymentMethod?.voucher?.required);
  const isPaymentMobileSelected = selectedPaymentMethod
    ? isPaymentMobileCode(selectedPaymentMethod.code)
    : false;

    useEffect(() => {
        if (!isStripeSelected) {
            setStripeController(null);
            setStripeStatus(DEFAULT_STRIPE_STATUS);
            setStripeExternalError(null);
        }
    }, [isStripeSelected]);

    const handleStripeReady = useCallback((controller: StripePaymentController | null) => {
        setStripeController(controller);
    }, []);

    const handleStripeStatusChange = useCallback((status: StripePaymentStatus) => {
        setStripeStatus(status);
        setStripeExternalError(null);
    }, []);

    const handleStripePaymentMethod = useCallback(async () => {
        if (!isStripeSelected) {
            return onSubmit();
        }

        if (!stripeController) {
            setStripeExternalError(checkoutCopy.payment.stripe.notReady);
            return;
        }

        const { paymentMethodId, error } = await stripeController.createPaymentMethod();

        if (!paymentMethodId || error) {
            setStripeExternalError(error ?? checkoutCopy.payment.stripe.tokenizationError);
            return;
        }

        await onSubmit(paymentMethodId);
    }, [isStripeSelected, onSubmit, stripeController]);

    const handleSubmit = useCallback(() => {
        void handleStripePaymentMethod();
    }, [handleStripePaymentMethod]);

    const isSubmitDisabled = useMemo(() => {
        if (!selectedPaymentMethodCode) {
            return true;
        }

        if (isProcessing) {
            return true;
        }

        if (isVoucherRequired && !voucherFile) {
            return true;
        }

        if (isStripeSelected) {
            if (!stripeController) {
                return true;
            }

            return !stripeStatus.isComplete || stripeStatus.hasError;
        }

        return false;
    }, [
        isProcessing,
        isStripeSelected,
        isVoucherRequired,
        selectedPaymentMethodCode,
        stripeController,
        stripeStatus.hasError,
        stripeStatus.isComplete,
        voucherFile,
    ]);

    return (
        <section className={styles.paymentSection} aria-labelledby="payment-methods-title">
            <div className={styles.paymentHeader}>
                <h2 id="payment-methods-title" className={styles.paymentTitle}>
                    {checkoutCopy.payment.heading}
                </h2>
                <p>{checkoutCopy.payment.description}</p>
            </div>

            <fieldset className={styles.paymentMethodsGroup}>
                <legend className={styles.visuallyHidden}>{checkoutCopy.payment.heading}</legend>
                <div className={styles.paymentMethods} role="radiogroup" aria-label={checkoutCopy.payment.heading}>
                    {paymentMethods.map((method) => {
                        const isSelected = method.code === selectedPaymentMethodCode;

                        return (
                            <div key={method.code} className={styles.paymentMethodWrapper}>
                                <PaymentMethodOption method={method} isSelected={isSelected} onSelect={onSelect}/>
                                {isSelected && selectedPaymentMethod && (
                                    <>
                                        <PaymentMethodTransferInfo transferInfo={selectedPaymentMethod.transferInfo}>
                                            {isPaymentMobileSelected && (
                                                <div className={styles.exchangeBox} role="status" aria-live="polite">
                                                    <p className={styles.exchangeLabel}>{checkoutCopy.payment.mobileAmountLabel}</p>
                                                    <p className={styles.exchangeValue}>
                                                        {isRateLoading || !exchangeRate
                                                            ? checkoutCopy.payment.exchangeRateLoading
                                                            : new Intl.NumberFormat(checkoutLocale, {
                                                                style: "currency",
                                                                currency: "VES",
                                                                maximumFractionDigits: 2,
                                                            }).format(order.amount.amount * exchangeRate)}
                                                    </p>
                                                    <p className={styles.exchangeRate}>
                                                        {rateError ??
                                                            `${checkoutCopy.payment.exchangeRateLabel}: ${exchangeRate?.toFixed(2) ?? "–"}`}
                                                    </p>
                                                </div>
                                            )}
                                        </PaymentMethodTransferInfo>
                                        {isStripeSelected && (
                                            <StripePaymentFields
                                                onReady={handleStripeReady}
                                                onStatusChange={handleStripeStatusChange}
                                                externalError={stripeExternalError}
                                            />
                                        )}
                                        <PaymentMethodForm
                                            fields={paymentFormFields}
                                            values={paymentFormValues}
                                            onChange={onFieldChange}
                                        />
                                        <PaymentVoucherUpload
                                            voucherConfig={selectedPaymentMethod.voucher}
                                            file={voucherFile}
                                            onChange={onVoucherChange}
                                        />
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            </fieldset>

            {actionError && (
                <p className={styles.actionError} role="alert">
                    {actionError}
                </p>
            )}
            {stripeExternalError && !actionError && (
                <p className={styles.actionError} role="alert">
                    {stripeExternalError}
                </p>
            )}

            <div className={styles.submitActions}>
                <button
                    type="button"
                    className={styles.payButton}
                    onClick={handleSubmit}
                    aria-label={checkoutCopy.payment.primaryAction}
                    disabled={isSubmitDisabled}
                    aria-busy={isProcessing}
                >
                    {isProcessing && <span className={styles.payButtonSpinner} aria-hidden="true"/>}
                    <span>{isProcessing ? checkoutCopy.payment.processing : checkoutCopy.payment.primaryAction}</span>
                </button>
            </div>
        </section>
    );
};
